import FarmersHubPage from "@/components/FarmersHubPage";
import {
  Warning, Cow, Books, Lightbulb, Bank, Storefront,
  Newspaper, Wrench, Leaf, Drop, Basket,
} from "@phosphor-icons/react";

/**
 * FarmersHubs — topical hub pages sharing the same shell layout.
 * These pages surface curated shortcuts + community CTAs while richer
 * back-end features roll in over subsequent phases.
 */

export function FarmersAlerts() {
  return (
    <FarmersHubPage
      testid="farmers-alerts-page"
      eyebrow="Farm Alerts"
      Icon={Warning}
      title="Alerts, tuned to your farm."
      subtitle="Frost, drought, heat, storm, hail, wildfire, pest, disease, market and government alerts — geographically targeted."
      intro="Alerts are triggered by community observations, weather feeds and threshold rules you set for each farm. Keep them local, keep them useful."
      sections={[
        {
          eyebrow: "Weather", title: "Extreme weather", to: "/farmers/weather",
          description: "Heavy rain, drought, heat waves, frost warnings, high wind, hail and flood risk for your coordinates.",
          linkLabel: "Open weather center",
        },
        {
          eyebrow: "Pests", title: "Pest & disease pressure", to: "/farmers/pest-heatmap",
          description: "Live heatmap of nearby pest and disease reports with threshold-based alerts.",
          linkLabel: "Open pest heatmap",
        },
        {
          eyebrow: "Community", title: "Nearby farmer reports", to: "/farmers/local",
          description: "See what neighbours are reporting — the earliest signal usually comes from other farmers.",
          linkLabel: "Open local pulse",
        },
        {
          eyebrow: "Market", title: "Market & government alerts", badge: "Coming next",
          description: "Price collapses, import bans, subsidy deadlines, extension programs — surfaced by region.",
        },
        {
          eyebrow: "Animal health", title: "Animal disease outbreaks", badge: "Coming next",
          description: "Regional livestock disease and quarantine alerts from official sources.",
        },
        {
          eyebrow: "Water", title: "Well & irrigation warnings", badge: "Coming next",
          description: "Watch groundwater, reservoir and canal alerts specific to your district.",
        },
      ]}
      callToAction={{
        eyebrow: "Farmers Pulse",
        title: "Report what you notice — every observation helps your neighbours.",
        description: "One report becomes another farmer's early warning.",
        label: "Report an observation",
        to: "/farmers/report",
      }}
    />
  );
}

export function FarmersLivestock() {
  return (
    <FarmersHubPage
      testid="farmers-livestock-page"
      eyebrow="Livestock & Poultry"
      Icon={Cow}
      title="Livestock, poultry & aquaculture."
      subtitle="Cattle, buffalo, goats, sheep, pigs, chickens, ducks, bees, aquaculture — a hub for animal husbandry."
      intro="Track your animals inside My Farm and share community observations that help other farmers spot disease, breeding and feed trends early."
      sections={[
        {
          eyebrow: "My animals", title: "Manage my roster", to: "/farmers/mine?tab=animals",
          description: "Add species, breed, count, purpose, housing and feed for every farm.",
          linkLabel: "Open my animals",
        },
        {
          eyebrow: "Observations", title: "Report animal health", to: "/farmers/report",
          description: "First calving, unusual mortality, disease symptoms, feed shortages, predator attacks — every note counts.",
          linkLabel: "Report what you see",
        },
        {
          eyebrow: "Community", title: "Livestock discussions", to: "/farmers/community",
          description: "Ask experienced farmers about feed, breeding schedules, vaccines and traditional practices.",
          linkLabel: "Ask farmers",
        },
        {
          eyebrow: "Aquaculture", title: "Fish, shrimp & prawn", badge: "Coming next",
          description: "Water quality logs, dissolved oxygen, feeding schedules and disease indicators.",
        },
        {
          eyebrow: "Bees & pollinators", title: "Beekeeping pulse", badge: "Coming next",
          description: "Hive counts, swarm reports and honeyflow observations for the local network.",
        },
        {
          eyebrow: "Breeds", title: "Regional breed catalog", badge: "Coming next",
          description: "Local breeds with adaptation notes — from Vechur cattle in Kerala to Boer goats.",
        },
      ]}
    />
  );
}

export function FarmersKnowledge() {
  return (
    <FarmersHubPage
      testid="farmers-knowledge-page"
      eyebrow="Farmer Knowledge"
      Icon={Books}
      title="A practical farmer's encyclopedia."
      subtitle="How to grow, when to plant, when to harvest, seed saving, grafting, pruning, composting, greenhouses, post-harvest storage."
      intro="Curated farmer-friendly explanations linking to the crop database, expert articles and research references."
      sections={[
        { eyebrow: "Growing", title: "How to grow", to: "/farmers/crops", description: "Every crop in the database has a planting window, temperature range, water and soil notes.", linkLabel: "Open crop database" },
        { eyebrow: "Timing", title: "When to plant · when to harvest", to: "/farmers/calendar", description: "Location-aware planting and harvest calendar keyed to your climate zone.", linkLabel: "Open calendar" },
        { eyebrow: "Soil", title: "Composting & fertility", to: "/farmers/soil", description: "Composting, cover crops, mulching, crop rotation, minimum tillage.", linkLabel: "Open soil hub" },
        { eyebrow: "Water", title: "Irrigation & conservation", to: "/farmers/water", description: "Drip, mulch, water harvesting and drought-tolerant practices.", linkLabel: "Open water hub" },
        { eyebrow: "Pests", title: "Pest & disease management", to: "/farmers/pest-heatmap", description: "IPM, natural predators, resistant varieties and safe use of chemicals.", linkLabel: "Open pest heatmap" },
        { eyebrow: "Seeds", title: "Seed saving & grafting", badge: "Coming next", description: "Choosing parents, drying, storage, viability testing, cleft and whip grafting." },
        { eyebrow: "Storage", title: "Post-harvest storage", badge: "Coming next", description: "Reducing losses in grains, tubers, fruit and vegetables — cold chain and dry storage." },
        { eyebrow: "Practices", title: "Organic & regenerative farming", badge: "Coming next", description: "Case studies from farmers transitioning to organic, regenerative and low-input farming." },
      ]}
    />
  );
}

export function FarmersResearch() {
  return (
    <FarmersHubPage
      testid="farmers-research-page"
      eyebrow="Research & Innovation"
      Icon={Lightbulb}
      title="Farmer-friendly research."
      subtitle="Climate-resilient crops, new varieties, soil science, precision agriculture, AI, regenerative techniques — translated for the field."
      intro="A curated bridge between agricultural science and everyday farming. Articles from NaturePulse Experts, universities and research institutions."
      sections={[
        { eyebrow: "Experts", title: "NaturePulse Experts", to: "/experts?topic=agriculture", description: "Read what verified agricultural experts and researchers are publishing.", linkLabel: "Browse experts" },
        { eyebrow: "Research hub", title: "Research requests & calls", to: "/experts/research-requests", description: "Community observation calls from working researchers.", linkLabel: "Open research requests" },
        { eyebrow: "Innovation", title: "Farmer innovation stories", badge: "Coming next", description: "Farmers using drip lines from recycled bottles, low-cost drone spraying, solar irrigation." },
        { eyebrow: "Blog", title: "Farmers Pulse blog", badge: "Coming next", description: "Editorial articles from the Farmers Pulse team and invited authors." },
        { eyebrow: "Universities", title: "University partners", badge: "Coming next", description: "Land-grant, ICAR, CGIAR and regional agricultural universities publishing here." },
        { eyebrow: "Papers", title: "Research digests", badge: "Coming next", description: "Plain-language summaries of new agricultural research — with citations." },
      ]}
    />
  );
}

export function FarmersGovernment() {
  return (
    <FarmersHubPage
      testid="farmers-government-page"
      eyebrow="Government & Assistance"
      Icon={Bank}
      title="Grants, subsidies, insurance & training."
      subtitle="Location-aware. Deadlines. Extension offices. Disaster assistance."
      intro="A geographically dynamic hub for government agricultural programs, subsidies, insurance schemes, disaster aid and training opportunities."
      sections={[
        { eyebrow: "Programs", title: "Government programs by region", badge: "Coming next", description: "Filter by country → state to find current schemes and deadlines." },
        { eyebrow: "Disaster", title: "Disaster assistance", badge: "Coming next", description: "After floods, drought, wildfire, storm damage — how to apply." },
        { eyebrow: "Insurance", title: "Crop insurance", badge: "Coming next", description: "Options for weather-index, area-yield and revenue-based coverage." },
        { eyebrow: "Extension", title: "Extension officers", badge: "Coming next", description: "Directory of nearest agricultural extension centers." },
        { eyebrow: "Training", title: "Farmer training", badge: "Coming next", description: "Workshops, KVK programs, online courses in local languages." },
        { eyebrow: "Loans", title: "Agricultural loans", badge: "Coming next", description: "KCC, cooperative credit, microfinance and rural bank options." },
      ]}
    />
  );
}

export function FarmersMarketplace() {
  return (
    <FarmersHubPage
      testid="farmers-marketplace-events-page"
      eyebrow="Farmers Market Events"
      Icon={Storefront}
      title="Real markets. Real events."
      subtitle="Farmers markets, farm-gate sales, seed swaps, harvest festivals, agricultural fairs — an events layer for the physical market."
      intro="A community-driven directory of upcoming farmers markets and rural events. E-commerce comes later — this is about the calendar of physical gatherings."
      sections={[
        { eyebrow: "Events", title: "Upcoming farmers markets", badge: "Coming next", description: "Weekly and monthly markets by district — day, time, address and organiser contact." },
        { eyebrow: "Swaps", title: "Seed & seedling swaps", badge: "Coming next", description: "Community seed exchanges keeping heirloom varieties alive." },
        { eyebrow: "Festivals", title: "Harvest festivals", badge: "Coming next", description: "Regional harvest, sowing and pollinator festivals." },
        { eyebrow: "Fairs", title: "Agricultural fairs", badge: "Coming next", description: "Trade fairs, breed shows, livestock exhibitions." },
        { eyebrow: "Directory", title: "Farm-gate sales", badge: "Coming next", description: "Farmers offering direct-from-farm produce with visiting hours." },
      ]}
    />
  );
}

export function FarmersEquipment() {
  return (
    <FarmersHubPage
      testid="farmers-equipment-page"
      eyebrow="Equipment & Technology"
      Icon={Wrench}
      title="Tools that pull their weight."
      subtitle="Tractors, implements, irrigation, drones, sensors, farm apps, AI tools, equipment sharing and rental."
      intro="A knowledge and directory hub for equipment. Real listings will be added as the community grows."
      sections={[
        { eyebrow: "Machinery", title: "Tractors & implements", badge: "Coming next", description: "Small, medium and large tractors, ploughs, planters and harvesters — with common maintenance tips." },
        { eyebrow: "Irrigation", title: "Irrigation gear", badge: "Coming next", description: "Drip lines, sprinklers, pumps, filters, solar-powered systems." },
        { eyebrow: "Drones & sensors", title: "Drones, cameras & sensors", badge: "Coming next", description: "Field imaging, soil moisture sensors, weather stations, wildlife cameras." },
        { eyebrow: "Sharing", title: "Equipment sharing / rental", badge: "Coming next", description: "Local rental listings from farmers and cooperatives." },
        { eyebrow: "Apps", title: "Farm apps & AI tools", badge: "Coming next", description: "Curated list of the most useful open and free tools for farmers." },
      ]}
    />
  );
}

export function FarmersNews() {
  return (
    <FarmersHubPage
      testid="farmers-news-page"
      eyebrow="Farm News"
      Icon={Newspaper}
      title="Agricultural news, sourced honestly."
      subtitle="AI-curated agriculture news from vetted publishers — always linking back to the original source."
      intro="News is grouped by geography and topic. Farmers Almanac and other trusted publishers are being wired into the discovery agent."
      sections={[
        { eyebrow: "All news", title: "NaturePulse agriculture feed", to: "/news?category=agriculture",
          description: "Every agriculture-tagged story on NaturePulse, refreshed by the discovery agent every 6 hours.", linkLabel: "Open feed" },
        { eyebrow: "Weather", title: "Weather & climate news", to: "/news?category=weather",
          description: "Extreme weather, seasonal forecasts, monsoon updates.", linkLabel: "Open feed" },
        { eyebrow: "Crops", title: "Crop-specific news", to: "/news?category=crops",
          description: "Rice, wheat, coffee, cocoa, tea, cotton, sugar and more.", linkLabel: "Open feed" },
        { eyebrow: "Water", title: "Water & irrigation news", to: "/news?category=freshwater",
          description: "Reservoirs, groundwater, irrigation policy.", linkLabel: "Open feed" },
        { eyebrow: "Livestock", title: "Livestock & aquaculture", to: "/news?category=agriculture",
          description: "Disease outbreaks, breed programs, fisheries news.", linkLabel: "Open feed" },
        { eyebrow: "Almanac", title: "Farmers Almanac feed", badge: "Coming next",
          description: "Practical seasonal advice, moon phases and long-range guidance from farmersalmanac.com." },
      ]}
    />
  );
}

export function FarmersSoil() {
  return (
    <FarmersHubPage
      testid="farmers-soil-page"
      eyebrow="Soil & Land"
      Icon={Leaf}
      title="Read the soil before you plant."
      subtitle="Soil types, pH, nutrients, composting, fertility, erosion, moisture, regenerative practices."
      intro="Soil is the beginning of every crop. Log your soil readings inside each farm and see aggregated regional patterns here."
      sections={[
        { eyebrow: "Per farm", title: "Soil Pulse on your farm", to: "/farmers/farms",
          description: "Log pH, N/P/K, organic matter, salinity and moisture. AI-extract from PDF lab reports.",
          linkLabel: "Open farm registry" },
        { eyebrow: "Composting", title: "Composting", badge: "Coming next",
          description: "Aerobic, vermicomposting, bokashi, farm-yard manure — matched to your soil type." },
        { eyebrow: "Fertility", title: "Fertility management", badge: "Coming next",
          description: "N-P-K balance, micro-nutrients, cover crops and green manures." },
        { eyebrow: "Erosion", title: "Erosion & conservation", badge: "Coming next",
          description: "Contour ploughing, bunds, terraces, mulching, agroforestry." },
        { eyebrow: "Regenerative", title: "Regenerative practices", badge: "Coming next",
          description: "Minimum tillage, cover cropping, integrated grazing, agroforestry." },
        { eyebrow: "Community", title: "Soil observations", to: "/farmers/report",
          description: "Report unusual soil signs — hardpan, salinity crusts, waterlogging — so others learn.", linkLabel: "Report what you see" },
      ]}
    />
  );
}

export function FarmersWater() {
  return (
    <FarmersHubPage
      testid="farmers-water-page"
      eyebrow="Water & Irrigation"
      Icon={Drop}
      title="Every drop counts."
      subtitle="Rainfall, wells, ponds, rivers, reservoirs, groundwater, irrigation scheduling, drought and flood alerts."
      intro="Water is the most variable input on the farm. Log your rainfall and irrigation inside each farm; see aggregated regional patterns here."
      sections={[
        { eyebrow: "Per farm", title: "Water Pulse on your farm", to: "/farmers/farms",
          description: "Well depth, rainfall, irrigation and ET tracking with year-over-year rollups.",
          linkLabel: "Open farm registry" },
        { eyebrow: "Weather", title: "Rainfall history & forecast", to: "/farmers/weather",
          description: "14-day forecast + last 12 months of rainfall from Open-Meteo.",
          linkLabel: "Open weather" },
        { eyebrow: "Irrigation", title: "Irrigation scheduling", badge: "Coming next",
          description: "Recommended windows based on rainfall, ET and crop water demand." },
        { eyebrow: "Conservation", title: "Water conservation", badge: "Coming next",
          description: "Drip, mulching, water harvesting, contour bunds, farm ponds." },
        { eyebrow: "Alerts", title: "Drought & flood alerts", to: "/farmers/alerts",
          description: "Live alerts + community reports for your region.", linkLabel: "Open alert center" },
        { eyebrow: "Community", title: "Water observations", to: "/farmers/report",
          description: "Report well levels, spring flow, unusually dry streams, canal outages.", linkLabel: "Report what you see" },
      ]}
    />
  );
}

export function FarmersObservationsHub() {
  return (
    <FarmersHubPage
      testid="farmers-observations-hub-page"
      eyebrow="Farm Observations"
      Icon={Basket}
      title="Document what you see."
      subtitle="Photograph. Date. Location. Crop. Description. Tags."
      intro="Every observation you contribute strengthens the almanac. Approved reports appear on the Farm Pulse Map and the Local Farm Pulse."
      sections={[
        { eyebrow: "New", title: "Report what you see", to: "/farmers/report",
          description: "Submit a farmer observation — photo, category, crop, severity and location.", linkLabel: "Open the form" },
        { eyebrow: "Map", title: "Farm Pulse Map", to: "/farmers/map",
          description: "World map of every approved farmer observation.", linkLabel: "Open the map" },
        { eyebrow: "Local", title: "Local Farm Pulse", to: "/farmers/local",
          description: "Drill down through country → state → district → village.", linkLabel: "Open local pulse" },
        { eyebrow: "Nearby", title: "Nearby observations", to: "/farmers?tab=nearby",
          description: "See what farmers within 50 km are reporting today.", linkLabel: "Open home" },
        { eyebrow: "Mine", title: "My observations", to: "/farmers/mine?tab=observations",
          description: "Your submitted observations, chronologically.", linkLabel: "Open My Farm" },
      ]}
    />
  );
}
